import { Directive, effect, ElementRef, inject, input, Renderer2, RendererStyleFlags2 } from '@angular/core';
import { AbilityService } from 'ability';
import { HelpService } from 'mbs-ui-shared@help/help.service';

/**
 * @directive HelpLinkDirective
 *
 * Dynamically applies a `href` to an anchor (`<a>`) element based on a provided help link key.
 * Also hides the element if the user lacks permission to view help content.
 *
 * Usage:
 * ```html
 * <a [helpLink]="'settings-page'">Help</a>
 * ```
 *
 * Behavior:
 * - If kbMode, component will not combine new href with helpPortal + helpLink
 * - If user has permission (`read` on `HelpMarketing`) and the help link is valid, sets `href`.
 * - If the user lacks permission, hides the element with `display: none !important`.
 * - If the help link is invalid or missing, hides the element and removes `href`.
 *
 * Requirements:
 * - Must be used on an `<a>` tag.
 * - Depends on `AbilityService` for permission check.
 * - Depends on `HelpService` to resolve actual help link URLs.
 */
@Directive({
  selector: '[helpLink]',
  standalone: true
})
export class HelpLinkDirective {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private ability = inject(AbilityService);
  private helpService = inject(HelpService);

  helpLink = input<string>('');
  kbMode = input<boolean>(false);

  constructor() {
    effect(() => {
      this.applyLinkLogic();
    });
  }

  private applyLinkLogic(): void {
    this.ensureIsAnchor();

    const element: HTMLElement = this.el.nativeElement;
    const canShow = this.ability.can('read', 'HelpMarketing');

    if (!canShow) {
      return void this.renderer.setStyle(element, 'display', 'none', RendererStyleFlags2.Important);
    }

    const href = this.getHref();

    if (href) {
      this.renderer.setAttribute(element, 'href', href);
      this.renderer.removeStyle(element, 'display');

      return;
    }

    this.renderer.removeAttribute(element, 'href');
    this.renderer.setStyle(element, 'display', 'none', RendererStyleFlags2.Important);
  }

  private ensureIsAnchor(): void {
    if (this.el.nativeElement.tagName.toLowerCase() !== 'a') {
      throw new Error('HelpLinkDirective can only be applied to <a> elements');
    }
  }

  private getHref(): string {
    if (this.kbMode()) {
      return this.helpLink();
    }

    return this.helpService.getHelpLink(this.helpLink());
  }
}
